import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  EMPTY,
  exhaustMap,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  tap,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  DRAFT_SURVEY_DELETED_MESSAGE,
  INITIAL_LOCATION_UPLOAD_STATE,
  SURVEY_MESSAGES,
  type LocaleChangeTargeting,
  type SurveyLocaleData,
  type SurveyMetadataFormState,
  StandardSurveyPayloadInput,
  SurveyMarginFormState,
} from '@ps/survey-utils';
import { SurveyService } from '@ps/survey-data-access';
import { Nullable } from '@ps/utils';

import { SubmittableForm, SurveyAudienceForm } from '../../utils';
import {
  actingUser,
  reportFormErrors,
} from '../core/utils/survey-save-pipeline.helper';
import { gatherSurveyFormContext } from '../core/utils/survey-form-context.helper';
import { marginGateForm, marginSnapshot } from './domain/margin-snapshot';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from './survey-creation-audience.actions';
import { selectMetadataForm } from './survey-creation-audience.selectors';
import { INITIAL_SURVEY_CREATION_PRICING_STATE } from '../insights/survey-creation-insights-state.constants';
import { INITIAL_SURVEY_MARGIN_FORM_STATE } from '../survey-creation-state.constants';

// Owns the create-only lifecycle — create is dispatched
// from the create stage alone (launch never creates), so it lives here.
@Injectable()
export class SurveyAudienceCreateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);
  private readonly _surveyService = inject(SurveyService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);

  public readonly createStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.createStandardSurvey),
      this._createSurveyPipeline(),
    ),
  );

  public readonly createSurveyAfterLocaleChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.createSurveyAfterLocaleChange),
      concatLatestFrom(() => this._store.select(selectMetadataForm)),
      exhaustMap(([{ locale, targeting, deleteSurveyId }, metadataForm]) =>
        this._localeSurveyInput(locale, targeting, metadataForm).mapOr(
          EMPTY,
          (input) => this._createSurvey(input, deleteSurveyId),
        ),
      ),
    ),
  );

  public readonly deleteReplacedSurvey$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          SurveyCreationAudienceApiActions.createStandardSurveySuccess,
          SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
        ),
        filter(
          (action): action is typeof action & { replacedSurveyId: number } =>
            !!action.replacedSurveyId,
        ),
        mergeMap(({ replacedSurveyId }) =>
          this._surveyService.deleteSurvey(replacedSurveyId).pipe(
            tap(() => this._toastService.success(DRAFT_SURVEY_DELETED_MESSAGE)),
            this.psCatchError(() => EMPTY, { notify: true }),
          ),
        ),
      ),
    { dispatch: false },
  );

  private _createSurveyPipeline(
    replacedSurveyId: Nullable<number> = null,
  ): OperatorFunction<Action, Action> {
    return (trigger$) =>
      trigger$.pipe(
        gatherSurveyFormContext(this._store),
        tap(({ audienceForm, marginForm }) =>
          reportFormErrors(
            this._toastService,
            this._createGateForms(audienceForm, marginForm),
            true,
          ),
        ),
        // Creating requires the absence of an id; saving requires one. A survey
        // doesn't have launch data before it exists, so launch validity isn't
        // part of the create gate — only the save gate checks it.
        filter(
          ({ surveyId, audienceForm, childrenCount, marginForm }) =>
            !surveyId &&
            this._isSubmittable(audienceForm, childrenCount, marginForm),
        ),
        map(({ audienceForm, marginForm, pricing, locationFileUpload }) => {
          const user = actingUser(this._authSelectors);
          const { metadata, qualifications, quotas } = Option.from(
            audienceForm.data,
          ).unwrap();

          return {
            metadata,
            pricing,
            userId: user.id,
            companyId: user.companyId,
            targeting: {
              qualifications,
              quotas,
              locationFileUpload,
            },
            enableRespondentStatusFilter: false,
            ...marginSnapshot(marginForm).mapOr({}, (snapshot) => ({
              margin: snapshot,
            })),
          } satisfies StandardSurveyPayloadInput;
        }),
        exhaustMap((input) => this._createSurvey(input, replacedSurveyId)),
      );
  }

  private _createSurvey(
    input: StandardSurveyPayloadInput,
    replacedSurveyId: Nullable<number>,
  ): Observable<Action> {
    return this._surveyService.createStandardSurvey(input).pipe(
      map((survey) => {
        this._toastService.success(SURVEY_MESSAGES.SAVED);
        return SurveyCreationAudienceApiActions.createStandardSurveySuccess({
          survey,
          replacedSurveyId,
        });
      }),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationAudienceApiActions.createStandardSurveyFailure({
              error: error.message,
            }),
          ),
        { notify: true },
      ),
    );
  }

  // Metadata from the form (next locale applied), retained targeting; the rest defaults.
  private _localeSurveyInput(
    locale: SurveyLocaleData,
    retained: LocaleChangeTargeting,
    metadataForm: SurveyMetadataFormState,
  ): Option<StandardSurveyPayloadInput> {
    return Option.from(metadataForm.value).map((metadata) => {
      const user = actingUser(this._authSelectors);
      return {
        metadata: {
          ...metadata,
          selectedCountries: [locale.countryCode],
          selectedLanguages: [locale.languageShortCode],
        },
        pricing: {
          ...INITIAL_SURVEY_CREATION_PRICING_STATE,
          feasibilityCpi: 0,
        },
        userId: user.id,
        companyId: user.companyId,
        targeting: {
          qualifications: [...retained.retainedQualifications],
          quotas: [...retained.retainedQuotas],
          locationFileUpload: INITIAL_LOCATION_UPLOAD_STATE,
        },
        enableRespondentStatusFilter: false,
        ...marginSnapshot(INITIAL_SURVEY_MARGIN_FORM_STATE).mapOr(
          {},
          (snapshot) => ({
            margin: snapshot,
          }),
        ),
      };
    });
  }

  private _isSubmittable(
    form: SurveyAudienceForm,
    childrenCount: number,
    marginForm: SurveyMarginFormState,
  ): boolean {
    return (
      this._createGateForms(form, marginForm).every((gate) => gate.valid) &&
      childrenCount === 1
    );
  }

  // A survey has no launch data before it exists, so the create gate is the
  // audience form plus the margin — never the launch form.
  private _createGateForms(
    audienceForm: SurveyAudienceForm,
    marginForm: SurveyMarginFormState,
  ): SubmittableForm[] {
    return [audienceForm, marginGateForm(marginForm)];
  }
}
